import { getLanguageDirection } from '@/i18n/supportedLanguages';
import { type PropsWithChildren, createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// SE1 (7.3b): antd-free half of the old provider — context + the document.dir
// locale effect (owned here for BOTH shells; see themeStore's direction note).
// The antd ConfigProvider/v3Theme half moved to ./V1AntdConfigScope (mounted
// by AppV1 statically and by v1-fallback scopes lazily under the v4 shell).

type ConfigType = {
  theme: 'light' | 'dark';
  mode: 'light' | 'dark';
  direction: 'ltr' | 'rtl';
  locale?: string;
};
type MainConfigContextType = {
  config: ConfigType;
  setConfig: (config: ConfigType) => void;
};

const defaultConfig: ConfigType = {
  mode: 'light',
  theme: 'light',
  direction: 'ltr',
  locale: 'en_US',
};

export const MainConfigContext = createContext<MainConfigContextType>({} as MainConfigContextType);

export const MainConfigContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [config, setConfig] = useState<ConfigType>(defaultConfig);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  useEffect(() => {
    const direction = getLanguageDirection(currentLanguage);
    setConfig((prev) => ({ ...prev, direction }));
    document.dir = direction;
  }, [currentLanguage]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      setConfig,
      config,
    }),
    [config]
  );

  return <MainConfigContext.Provider value={contextValue}>{children}</MainConfigContext.Provider>;
};
